// frontend/src/pages/admin/AdminCatalog.js
import React, { useState, useEffect } from 'react';
import { catalogApi } from '../../api/catalog';
import { FaTags, FaCopyright, FaPlus, FaEdit, FaTrash, FaTimes, FaCheck, FaSpinner, FaSearch } from 'react-icons/fa';

function AdminCatalog() {
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' or 'brands'
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [catForm, setCatForm] = useState({ name: '', keywords: '', order: 0, is_active: true });
  const [brandForm, setBrandForm] = useState({ name: '', is_oem: false, origin: '' });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let result;
      if (activeTab === 'categories') {
        result = await catalogApi.getAllCategories();
      } else {
        result = await catalogApi.getAllBrands();
      }
      setData(result);
    } catch (err) {
      console.error(err);
      alert("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenModal = (item = null) => {
    setEditingItem(item);
    if (activeTab === 'categories') {
      if (item) {
        setCatForm({
          name: item.name,
          keywords: item.keywords ? item.keywords.join(', ') : '',
          order: item.order || 0,
          is_active: item.is_active
        });
      } else {
        setCatForm({ name: '', keywords: '', order: 0, is_active: true });
      }
    } else {
      if (item) {
        setBrandForm({
          name: item.name,
          is_oem: item.is_oem || false,
          origin: item.origin || ''
        });
      } else {
        setBrandForm({ name: '', is_oem: false, origin: '' });
      }
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (activeTab === 'categories') {
        const payload = {
          ...catForm,
          keywords: catForm.keywords.split(',').map(k => k.trim()).filter(k => k !== ''),
          order: parseInt(catForm.order)
        };
        if (editingItem) {
          await catalogApi.updateCategory(editingItem.id, payload);
        } else {
          await catalogApi.createCategory(payload);
        }
      } else {
        const payload = { ...brandForm };
        if (editingItem) {
          await catalogApi.updateBrand(editingItem.id, payload);
        } else {
          await catalogApi.createBrand(payload);
        }
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      alert(`Failed to save: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      if (activeTab === 'categories') {
        await catalogApi.deleteCategory(id);
      } else {
        await catalogApi.deleteBrand(id);
      }
      setData(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      alert("Failed to delete item.");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Catalog Management</h1>

      {/* TABS */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
        <button 
          onClick={() => setActiveTab('categories')}
          className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'categories' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          <FaTags /> Categories
        </button>
        <button 
          onClick={() => setActiveTab('brands')}
          className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'brands' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
        >
          <FaCopyright /> Brands
        </button>
      </div>

      {/* ACTIONS BAR - Updated for Mobile Responsiveness */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-white p-4 rounded shadow-sm gap-4">
        <div className="relative w-full md:w-64">
          <input 
            type="text" 
            placeholder={`Search ${activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded focus:outline-none focus:border-primary"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-full md:w-auto bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded flex items-center justify-center gap-2 font-bold"
        >
          <FaPlus /> Add {activeTab === 'categories' ? 'Category' : 'Brand'}
        </button>
      </div>

      {/* TABLE - Added Horizontal Scroll */}
      <div className="bg-white rounded shadow-sm overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : (
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="p-4">Name</th>
                {activeTab === 'categories' ? (
                  <>
                    <th className="p-4">Keywords</th>
                    <th className="p-4">Order</th>
                    <th className="p-4">Status</th>
                  </>
                ) : (
                  <>
                    <th className="p-4">Type</th>
                    <th className="p-4">Origin</th>
                  </>
                )}
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="p-4 font-bold text-gray-800">{item.name}</td>
                  
                  {activeTab === 'categories' ? (
                    <>
                      <td className="p-4 text-sm text-gray-600 max-w-xs truncate">
                        {item.keywords ? item.keywords.join(', ') : '-'}
                      </td>
                      <td className="p-4 text-sm">{item.order}</td>
                      <td className="p-4">
                        {item.is_active ? (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Active</span>
                        ) : (
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Inactive</span>
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-4">
                        {item.is_oem ? (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-bold">OEM</span>
                        ) : (
                          <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">Aftermarket</span>
                        )}
                      </td>
                      <td className="p-4 text-sm">{item.origin || '-'}</td>
                    </>
                  )}

                  <td className="p-4 text-right">
                    <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:bg-blue-50 p-2 rounded mr-2"><FaEdit /></button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:bg-red-50 p-2 rounded"><FaTrash /></button>
                  </td>
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500">No items found.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h3 className="text-lg font-bold">
                {editingItem ? 'Edit' : 'Create'} {activeTab === 'categories' ? 'Category' : 'Brand'}
              </h3>
              <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {activeTab === 'categories' ? (
                <>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Name</label>
                    <input 
                      value={catForm.name} 
                      onChange={e => setCatForm({...catForm, name: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Keywords (Comma separated)</label>
                    <input 
                      value={catForm.keywords} 
                      onChange={e => setCatForm({...catForm, keywords: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="e.g. pads, discs, stopping"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Display Order</label>
                      <input 
                        type="number"
                        value={catForm.order} 
                        onChange={e => setCatForm({...catForm, order: e.target.value})}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={catForm.is_active}
                          onChange={e => setCatForm({...catForm, is_active: e.target.checked})}
                          className="h-5 w-5 text-primary rounded"
                        />
                        <span className="text-sm font-medium">Active</span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Brand Name</label>
                    <input 
                      value={brandForm.name} 
                      onChange={e => setBrandForm({...brandForm, name: e.target.value})}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Origin Country</label>
                    <input 
                      value={brandForm.origin} 
                      onChange={e => setBrandForm({...brandForm, origin: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="e.g. Japan, Germany"
                    />
                  </div>
                  <div className="pt-2">
                    <label className="flex items-center gap-2 cursor-pointer bg-blue-50 p-3 rounded border border-blue-100">
                      <input 
                        type="checkbox" 
                        checked={brandForm.is_oem}
                        onChange={e => setBrandForm({...brandForm, is_oem: e.target.checked})}
                        className="h-5 w-5 text-primary rounded"
                      />
                      <div>
                        <span className="block text-sm font-bold text-blue-800">Is OEM?</span>
                        <span className="text-xs text-blue-600">Check if this is an Original Equipment Manufacturer.</span>
                      </div>
                    </label>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center gap-2">
                  {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />} Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCatalog;